import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'src/state/redux';
import { StyleSheet } from 'react-native';
import { generateClient } from 'aws-amplify/api';
import { createTenantUser, updateUserLambda } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import {
  DNABox,
  DNAButton,
  DNACard,
  DNACheckbox,
  DNASpinner,
  DNAText,
  GenericToast,
  Iffy,
  luxColors,
  ToastOrientations,
  useToast,
} from '@alucio/lux-ui';
import {
  CustomFieldDefinition,
  CustomFieldUsage,
  FieldDataType,
  User,
  UserRole,
} from '@alucio/aws-beacon-amplify/src/models';
import {
  USER_ROLE,
  CustomValuesInput,
  UpdateUserLambdaInput,
  CreateUserLambdaInput,
} from '@alucio/aws-beacon-amplify/src/API';
import colors from '@alucio/lux-ui/lib/theming/themes/alucio/colors';
import UserFilter, { CONFIG_FIELDS, FilterValue, LOCKED_FILTERS } from './UserFilter';

import DNACloseUserDrawerConfirmation from '../DNA/Modal/DNACloseUserDrawerConfirmation';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { useTenant, useTenantCustomFields } from 'src/state/redux/selector/tenant';
import { useCurrentUserORM, useUserById, useUsersByTenantId } from 'src/state/redux/selector/user';
import { ComposableField } from '../CustomFields/ComposableField';
import { fields } from 'src/hooks/useUserEditFormConfig/UserEditConfig';
import { ComposableForm, useComposableForm } from '../CustomFields/ComposableForm';
import useUserEditFormConfig from 'src/hooks/useUserEditFormConfig/useUserEditFormConfig';
import { getExceededLicenseType } from 'src/utils/licenseValidation';
import cloneDeep from 'lodash/cloneDeep'

const styles = StyleSheet.create({
  commonPadding: {
    paddingVertical: 16,
    paddingHorizontal: 32,
  },
  content: {
    backgroundColor: colors['color-gray-10'],
    flex: 1,
    paddingHorizontal: 32,
    paddingVertical: 34,
    overflow: 'scroll',
  },
  footer: {
    borderTopColor: colors['color-gray-100'],
    borderTopWidth: 1,
  },
  header: {
    borderBottomColor: colors['color-gray-100'],
    borderBottomWidth: 1,
  },
  inputColor: {
    backgroundColor: luxColors.info.primary,
  },
  inputStyle: {
    backgroundColor: luxColors.info.primary,
    borderColor: colors['color-gray-100'],
  },
  inputStyleError: {
    backgroundColor: luxColors.info.primary,
    borderColor: colors['color-danger-500'],
  },
  roleInputError: {
    backgroundColor: luxColors.info.primary,
    borderColor: colors['color-danger-500'],
    borderRadius: 5,
    borderWidth: 1,
  },
  titleColor: {
    color: luxColors.contentText.quaternary,
  },
});

interface Props {
  tenantId?: string,
  userId?: string,
  toggleDrawer: () => void,
}

interface FooterProps {
  isCreate: boolean,
  closeDrawer: () => void,
  isSubmitting: boolean,
  isSubmitSuccessful: boolean,
  sendInvite: boolean,
  setSendInvite: (sendInvite: boolean) => void,
  onSave: () => void,
  userId?: string,
}

interface HeaderProps {
  closeDrawer: () => void,
  userEmail?: string,
}

interface ConfigValuesType {
  [key: string]: FilterValue
}

const getInitialConfigValues = (customFields: CustomFieldDefinition[], user?: User): ConfigValuesType => {
  const filterValues = {};

  // WE FORMAT THE FIELDVALUES TO BE HANLDED INDIVIDUALLY, USING THE FOLLOWING STRUCTURE:
  // { [key as string]: { defaultFilterValues: string[], lockedFilters: string[] } }
  customFields.forEach((field) => {
    filterValues[field.id] = {
      defaultFiltersCustomValues: [],
      lockedFiltersCustomValues: [],
    };

    // IF WE'RE EDITING, WE GET THE VALUES THE USER HAS
    filterValues[field.id].defaultFiltersCustomValues = user?.defaultFiltersCustomValues
      .find(({ fieldId }) => fieldId === field.id)?.values || [];
    filterValues[field.id].lockedFiltersCustomValues = user?.lockedFiltersCustomValues
      .find(({ fieldId }) => fieldId === field.id)?.values || [];
  });

  return filterValues
}

export const DNAUserEdit = (props: Props) => {
  const { toggleDrawer } = props;
  const userORM = useUserById(props.userId || '');
  const toast = useToast();
  const user = userORM?.model;
  const editorUser = useCurrentUserORM();
  const tenant = useTenant(user?.tenantId || props.tenantId!);
  const tenantUsers = useUsersByTenantId(user?.tenantId || props.tenantId!);
  const userFilterFields = useTenantCustomFields({
    tenantId: tenant?.tenant.id,
    usages: { internalUsages: [CustomFieldUsage.USER_FILTER] },
    fieldDataType: [FieldDataType.CATEGORICAL, FieldDataType.MULTICATEGORICAL],
  });

  const dispatch = useDispatch();

  const initialConfigValues = getInitialConfigValues(userFilterFields, user)
  const [sendInvite, setSendInvite] = useState<boolean>(true);
  const [configValues, setConfigValues] = useState<ConfigValuesType>(initialConfigValues)
  const onSubmit = async (values) => {
    // LICENSING VALIDATION
    const candidateUser = {
      id: user?.id,
      email: values.email,
      role: values.role,
      status: user?.status || 'ACTIVE',
      tenantId: user?.tenantId || tenant?.tenant.id,
    };
    const exceededLicenseType = getExceededLicenseType(
      tenantUsers.map((tenantUser) => tenantUser.model),
      candidateUser,
      {
        standard: tenant?.tenant.licensedUsers,
        viewOnly: tenant?.tenant.licensedViewOnlyUsers,
      },
      user,
    );

    if (exceededLicenseType) {
      const title = exceededLicenseType === 'VIEW_ONLY'
        ? 'View-only user limit has been reached'
        : 'User limit has been reached';

      toast?.add(
        <GenericToast title={title} status="error" />,
        ToastOrientations.TOP_RIGHT,
      );
      return;
    }

    const { defaultFiltersCustomValues, lockedFiltersCustomValues } =
      getFormattedFilterValuesFromState(configValues);

    const additionalRoles: USER_ROLE[] = []
    if (values.TeamReportViewer === 'YES') {
      additionalRoles.push(USER_ROLE.TeamReportViewer)
    }
    if (values.LeadViewer === 'YES' && values.role === USER_ROLE.TenantViewer) {
      additionalRoles.push(USER_ROLE.LeadViewer);
    }
    if (values.TenantAdmin === 'YES') {
      additionalRoles.push(USER_ROLE.TenantAdmin)
    }

    // For removing the tenant admin in additional roles if checkbox is not visible
    const isTenantPublisher = values.role === USER_ROLE.TenantPublisher;
    const processedAdditionalRoles = isTenantPublisher
      ? additionalRoles
      : additionalRoles.filter((role) => role !== USER_ROLE.TenantAdmin);

    // Common fields for both create and update
    const commonPayload = {
      email: values.email?.toLowerCase(),
      givenName: values.givenName,
      familyName: values.familyName,
      role: USER_ROLE[values.role],
      tenantId: user?.tenantId || tenant?.tenant.id,
      defaultFiltersCustomValues,
      lockedFiltersCustomValues: isTenantPublisher ? [] : lockedFiltersCustomValues,
      defaultFilterValues: user?.defaultFilterValues || [],
      lockedFilters: user?.lockedFilters || [],
      // The value of isExcludedFromReporting is 'YES'/'NO' instead of true/false because of how the composable field is setup
      isExcludedFromReporting: values.isExcludedFromReporting === 'YES',
      additionalRoles: processedAdditionalRoles,
      altUsername: values.altUsername?.toLowerCase(),
    };

    try {
      if (!commonPayload.tenantId) {
        throw new Error('Tenant ID is required');
      }

      if (user) {
        // Update existing user
        const updatePayload: UpdateUserLambdaInput = {
          id: user.id,
          ...commonPayload,
        };

        const appsyncClient = generateClient();
        await appsyncClient.graphql({
          query: updateUserLambda,
          variables: { user: updatePayload },
        });
        // Tracking for editing a user
        analytics.track('ADMIN_UPDATE_USER', {
          category: 'ADMIN',
          action: 'EDIT',
        });
      } else {
        // Create new user
        const createPayload: CreateUserLambdaInput = {
          ...commonPayload,
          tenantId: commonPayload.tenantId!, // Required for create
          email: commonPayload.email!, // Required for create
          givenName: commonPayload.givenName!, // Required for create
          familyName: commonPayload.familyName!, // Required for create
          role: commonPayload.role!, // Required for create
        };
        const appsyncClient = generateClient();
        await appsyncClient.graphql({
          query: createTenantUser,
          variables: { user: createPayload, sendInvite },
        });
        // Tracking for creating a user
        analytics.track('ADMIN_CREATE_USER', {
          category: 'ADMIN',
          action: 'CREATE',
        });
        toast?.add(
          <GenericToast title={`New user created. ${sendInvite ? 'Email sent.' : ''}`} status="success" />,
          ToastOrientations.TOP_RIGHT,
        );
        toggleDrawer();
      }
    } catch (e: any) {
      const errorMessage = e.errors?.[0]?.message;

      // this is not the best way of setting the error because if the error message changes from the api call it wont show on the front end
      // we should look at modifying composable field to pass error messages from api calls to display in the form
      // note: THE API for update and create sends different messages for when the email exists.
      if (errorMessage === 'This email is already in use.' || errorMessage === 'This email already exists.') {
        rhForm.setError('email', { message: errorMessage }, { shouldFocus: true })
      } else if (errorMessage === 'This username is already in use as a primary email.') {
        rhForm.setError('altUsername', { message: errorMessage }, { shouldFocus: true })
      }

      if (errorMessage) {
        toast?.add(
          <GenericToast title="Something went wrong" status="error" />,
          ToastOrientations.TOP_RIGHT,
        );
        return;
      }
    }

    setTimeout(() => {
      // we reset because we want to reset the form states such as isSubmitSuccessful but still want to keep new values that user has edited
      rhForm.reset(undefined, { keepValues: true })
    }, 2000);
  }

  // react-hook-form
  const { rhForm } = useComposableForm()
  const selectedRole = rhForm.watch('role')

  // Validate email/altUsername cross-validation
  useEffect(() => {
    if (rhForm.formState.dirtyFields.altUsername || rhForm.formState.dirtyFields.email) {
      rhForm.trigger('altUsername')
      rhForm.trigger('email')
    }
  }, [
    rhForm.formState.dirtyFields.altUsername,
    rhForm.formState.dirtyFields.email,
    rhForm.watch('altUsername'),
    rhForm.watch('email'),
  ])

  const isEditingSelf = editorUser?.model.id === user?.id;

  // MEMOIZED COMPONENTS/FUNCTIONS
  const HeaderMemoized = useMemo(
    () => <Header closeDrawer={onCancel} userEmail={user?.email} />,
    [user?.email, rhForm.formState.isDirty],
  )
  const FooterMemoized = useMemo(
    () => (
      <Footer
        setSendInvite={setSendInvite}
        sendInvite={sendInvite}
        closeDrawer={onCancel}
        onSave={handleSubmitForm}
        isCreate={!user}
        userId={user?.id}
        isSubmitting={rhForm.formState.isSubmitting}
        isSubmitSuccessful={rhForm.formState.isSubmitSuccessful}
      />
    ),
    [
      rhForm.formState.isDirty,
      rhForm.formState.isSubmitting,
      rhForm.formState.isSubmitSuccessful,
      sendInvite,
      user,
      onCancel,
    ],
  );

  const onChangeFilterOption = useCallback(
    (
      fieldId: string,
      fieldValueId: string,
      column: CONFIG_FIELDS,
    ) => onFilterOptionSelected(fieldId, fieldValueId, column),
    [configValues],
  );
  const onAllValuesSelectedMemoized = useCallback(
    (
      fieldId: string,
      column: CONFIG_FIELDS,
      values: string[],
      checked: boolean,
    ) => onAllValuesSelected(fieldId, column, values, checked),
    [configValues],
  );

  function onFilterOptionSelected(fieldId: string, fieldValueId: string, column: CONFIG_FIELDS): void {
    const configValuesCopy = cloneDeep(configValues)
    // IF THE VALUE IS ALREADY IN THE ARRAY, WE REMOVE IT. OTHERWISE WE ADD IT
    if (configValuesCopy[fieldId][column].includes(fieldValueId)) {
      configValuesCopy[fieldId][column] = configValuesCopy[fieldId][column].filter(value => value !== fieldValueId)
    } else {
      configValuesCopy[fieldId][column].push(fieldValueId)
    }

    // IF THE SELECTED ELEMENT IS IN THE LOCKED FILTERS COLUMN, AND WE'RE UNCHECKING,
    // WE NEED TO REMOVE IT FROM THE DEFAULT FILTER VALUES
    if (column === LOCKED_FILTERS && configValuesCopy[fieldId].lockedFiltersCustomValues.length) {
      configValuesCopy[fieldId].defaultFiltersCustomValues =
        configValuesCopy[fieldId].defaultFiltersCustomValues.filter(
          (value) => configValuesCopy[fieldId].lockedFiltersCustomValues.includes(value),
        );
    }

    setConfigValues(configValuesCopy)
  }

  // SELECTS OR DESELECTS EVERY VALUE OF A SPECIFIC FIELD
  function onAllValuesSelected(fieldName: string, column: CONFIG_FIELDS, values: string[], checked: boolean): void {
    const configValue = configValues[fieldName];
    let newSelectedValues: string[] = [];

    if (column === LOCKED_FILTERS) {
      newSelectedValues = checked ? values : [];
    } else {
      if (!checked) {
        newSelectedValues = [];
      } else {
        // IF THE CHECKED IS TRUE, WE CAN ONLY SELECT (FOR THE DEFAULTS) THE ONES ALLOWED BY THE LOCKED ONES
        newSelectedValues = configValue.lockedFiltersCustomValues.length
          ? [...configValue.lockedFiltersCustomValues] : [...values];
      }
    }

    setConfigValues(
      {
        ...configValues,
        [fieldName]: {
          ...configValues[fieldName],
          [column]: newSelectedValues,
        },
      });
  }

  function onCancel(): void {
    // Only show the confirmation modal if there are unsaved changes and the form was not just saved successfully
    if (rhForm.formState.isDirty && !rhForm.formState.isSubmitSuccessful) {
      const payload = {
        isVisible: true,
        allowBackdropCancel: false,
        component: () => <DNACloseUserDrawerConfirmation closeUserDrawer={toggleDrawer} />,
      };
      dispatch(DNAModalActions.setModal(payload));
    } else {
      toggleDrawer();
    }
  }

  function handleSubmitForm(): void {
    rhForm.handleSubmit(onSubmit)()
  }

  return (
    <DNABox appearance="col" style={{ flex: 1 }}>
      {/* HEADER */}
      {HeaderMemoized}
      {/* CONTENT */}
      <DNABox style={styles.content} appearance="col" spacing="lg">
        <DNABox appearance="col">
          <DNACard
            appearance="modal"
          >
            <DNAText style={[{ marginBottom: 26 }, styles.titleColor]}>USER INFORMATION</DNAText>
            <DNABox spacing="md" childStyle={{ flex: 1 }}>
              <DNABox appearance="col" spacing="md">
                {/* First name */}
                <DNABox appearance="col" spacing="sm">
                  <ComposableField
                    key={fields[0].fieldName}
                    field={fields[0]}
                  />
                </DNABox>
                {/* Last name */}
                <DNABox appearance="col" spacing="sm">
                  <ComposableField
                    key={fields[1].fieldName}
                    field={fields[1]}
                  />
                </DNABox>
                {/* Mapped roles */}
                <DNABox appearance="col" spacing="sm">
                  <DNABox appearance="col" spacing="xs">
                    <ComposableField
                      key={fields[2].fieldName}
                      field={fields[2]}
                      disabled={rhForm.formState.isSubmitting || isEditingSelf}
                    />
                    <Iffy is={selectedRole === UserRole.TENANT_VIEWER}>
                      <ComposableField
                        key={fields[8].fieldName}
                        field={fields[8]}
                        showFieldTitle={false}
                      />
                    </Iffy>
                    <Iffy is={selectedRole === UserRole.TENANT_PUBLISHER}>
                      <ComposableField
                        key={fields[7].fieldName}
                        field={fields[7]}
                        showFieldTitle={false}
                      />
                    </Iffy>
                    <Iffy
                      is={
                        [
                          UserRole.TENANT_VIEWER,
                          UserRole.TENANT_PUBLISHER,
                        ].includes(selectedRole as UserRole)}
                    >
                      <ComposableField
                        key={fields[6].fieldName}
                        field={fields[6]}
                        showFieldTitle={false}
                      />
                    </Iffy>
                  </DNABox>
                </DNABox>
                {/* Email */}
                <DNABox appearance="col" spacing="sm">
                  <ComposableField
                    key={fields[3].fieldName}
                    field={fields[3]}
                  />
                </DNABox>
                {/* Add alternate username field */}
                <DNABox appearance="col" spacing="sm">
                  <ComposableField
                    key={fields[4].fieldName}
                    field={fields[4]}
                  />
                </DNABox>
              </DNABox>
            </DNABox>
          </DNACard>
        </DNABox>
        { /* USER FILTERS */}
        <DNABox appearance="col">
          <DNACard
            appearance="modal"
          >

            <DNAText style={[{ marginBottom: 26 }, styles.titleColor]}>USER FILTERS</DNAText>
            <DNABox appearance="col">
              {
                userFilterFields.map((field) => (
                  <DNABox key={field.id} appearance="col" spacing="xs">
                    <UserFilter
                      fieldDefinition={field}
                      disabled={rhForm.formState.isSubmitting}
                      values={configValues[field.id]}
                      onValueSelected={onChangeFilterOption}
                      onAllValuesSelected={onAllValuesSelectedMemoized}
                      disableLockedFilters={false}
                    />
                  </DNABox>
                ))
              }
            </DNABox>
          </DNACard>
        </DNABox>
        {/* exclude from reporting */}
        <DNABox>
          <DNACard
            appearance="modal"
          >
            <ComposableField
              key={fields[5].fieldName}
              field={fields[5]}
            />
          </DNACard>
        </DNABox>
      </DNABox>
      {/* FOOTER */}
      {FooterMemoized}
    </DNABox>
  );
};

function Footer(props: FooterProps): ReactElement {
  return (
    <DNABox style={[styles.commonPadding, styles.footer]} spacing="between" alignX="end" alignY="center">
      <DNABox spacing="sm">
        <Iffy is={props.isCreate}>
          <DNABox spacing="sm">
            <DNACheckbox
              disabled={props.isSubmitting}
              onChange={() => props.setSendInvite(!props.sendInvite)}
              checked={props.sendInvite}
            />
            <DNAText>Send email invite</DNAText>
          </DNABox>
        </Iffy>

        {props.userId && <DNAText status="subtle">User ID: {props.userId}</DNAText>}

      </DNABox>
      <DNABox spacing="sm">
        <DNAButton
          appearance="outline"
          onPress={props.closeDrawer}
          children="Cancel"
          disabled={props.isSubmitting}
        />
        <DNAButton
          {...(props.isSubmitSuccessful) ? { status: 'success', iconLeft: 'check' } : {}}
          onPress={props.onSave}
        >
          {props.isSubmitting
            ? <DNASpinner size="xs" status="info" />
            : props.isSubmitSuccessful
              ? 'Saved'
              : props.isCreate ? 'Create' : 'Save'}
        </DNAButton>
      </DNABox>
    </DNABox>
  );
}

function Header(props: HeaderProps): ReactElement {
  return (
    <DNABox style={[styles.commonPadding, styles.header]} spacing="between" alignY="center">
      <DNAText bold>{props.userEmail ? `Edit ${props.userEmail}` : 'Add user'}</DNAText>
      <DNABox alignY="center" spacing="sm">
        <DNAText status="danger">* Required Fields</DNAText>
        <DNAButton
          status="tertiary"
          appearance="ghost"
          iconLeft="x"
          onPress={props.closeDrawer}
        />
      </DNABox>
    </DNABox>
  );
}

// Since we are saving the custom fields in a the component level state we call this func in the onSubmit to prepare the payload
function getFormattedFilterValuesFromState(configValues: ConfigValuesType) {
  return Object.keys(configValues).reduce((acc, fieldId) => {
    if (configValues[fieldId].lockedFiltersCustomValues.length) {
      acc.lockedFiltersCustomValues.push({
        fieldId,
        values: [...configValues[fieldId].lockedFiltersCustomValues],
      });
    }

    if (configValues[fieldId].defaultFiltersCustomValues.length) {
      acc.defaultFiltersCustomValues.push({
        fieldId,
        values: [...configValues[fieldId].defaultFiltersCustomValues],
      });
    }

    return acc;
  }, {
    lockedFiltersCustomValues: [] as CustomValuesInput[],
    defaultFiltersCustomValues: [] as CustomValuesInput[],
  });
}

interface UserEditWrapperProps {
  toggleDrawer: () => void
  userId?: string
  tenantId?: string
}

const UserEditWrapper: React.FC<UserEditWrapperProps> = (props) => {
  const {
    toggleDrawer,
    userId,
    tenantId,
  } = props

  const formConfig = useUserEditFormConfig(userId)

  return (
    <ComposableForm {...formConfig}>
      <DNAUserEdit toggleDrawer={toggleDrawer} tenantId={tenantId} userId={userId} />
    </ComposableForm>
  )
}

export default UserEditWrapper;
